import React, { CSSProperties } from 'react'

import { DefaultVariableResult } from '@makyoapp/makyo-common'
import {
  BaseComponent,
  ColorVariant,
  CornerRadius,
  createFormThemeVariables,
  HoverEffectVariant,
  parseCornerRadius,
  RawSpacingInput,
  TW_UI_CSS_VARIABLES,
} from '@makyoapp/makyo-tw-ui'

import { IViewComponent } from '@/core/app-builder/app/component'
import { CoreComponentBrandkit, CoreComponentInfos } from '@/core/models/app-components/types'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { BRANDKIT_ALL_COLOR_OPTIONS, getAdaptiveColorV2 } from '@/shared/brand-kit'
import { ComponentSettingKey } from '@/shared/builder'
import {
  sectionBackgroundGenerators,
  SectionBackgroundGroupProps,
  SectionColorVariant,
} from '@/shared/builder/default/section.default'
import { getSafely } from '@/shared/safely'

import { BaseCompSectionProps, CompSectionSettings } from './comp'

function formatVerticalAlign(align: string): string {
  // convert top -> start, center -> center, bottom -> end
  if (align === 'top') return 'start'
  if (align === 'middle') return 'center'
  if (align === 'bottom') return 'end'

  return ['start', 'center', 'end'].includes(align) ? (align ?? 'center') : 'center'
}

export interface CompSectionWrapperProps {
  core: CoreComponentInfos
  brandkit: CoreComponentBrandkit
  children: (props: BaseCompSectionProps & { component: IViewComponent }) => React.ReactNode
}

/**
 * CompSectionWrapperCore
 *
 * @todo extract utils/types from section.default & SpacingInput
 */
export const CompSectionWrapperCore = ({
  core: { component, settings, containerClient, detailTableId, detailRowIndex, currentParentBg },
  brandkit: { mode },
  children,
}: CompSectionWrapperProps) => {
  // ========================= data =========================
  const bgImg = useParseVariableSelect<string>(
    settings?.[ComponentSettingKey.SectionDesignBackgroundImage],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  // ========================= design =========================

  // base
  const widthPreset = settings[ComponentSettingKey.SectionDesignWidthPreset] ?? '100'
  const width = `${widthPreset}%`

  const height = getSafely(settings, [ComponentSettingKey.SectionDesignHeight], [400])[0]
  const heightMode = settings[ComponentSettingKey.SectionDesignHeightMode] ?? 'auto'
  const alignment = settings[ComponentSettingKey.SectionDesignHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.SectionDesignMargin] as RawSpacingInput
  const padding = settings?.[ComponentSettingKey.SectionDesignPadding] as RawSpacingInput

  const hoverEffect = settings[ComponentSettingKey.SectionDesignHoverEffect] as HoverEffectVariant

  // section
  const cornerRadius = (settings?.[ComponentSettingKey.SectionDesignShape] || 'none') as CornerRadius
  const opacity = (settings?.[ComponentSettingKey.SectionDesignOverlayOpacity] as number[])?.[0]
  const gap = settings?.[ComponentSettingKey.SectionDesignGap] as { value: string; unit: string }
  const itemGap = gap ? `${gap.value}${gap.unit}` : '0px'
  const layout = settings?.[ComponentSettingKey.SectionDesignLayout] as number[]
  const orientation = settings[ComponentSettingKey.SectionDesignOrientation] ?? 'vertical'
  const sameHeight = settings[ComponentSettingKey.SectionDesignSameHeight] ?? false
  const verticalAlign = formatVerticalAlign(settings[ComponentSettingKey.SectionDesignVAlign] as string)

  const horizontalAlign = settings[ComponentSettingKey.SectionDesignHorizontalAlign] ?? 'center'
  // const maxItems = settings[ComponentSettingKey.SectionDesignMaxItems] ?? 1
  const backgroundColorPage = containerClient?.settings._Settings._BackgroundColor
  const bgColorVariant = settings?.[ComponentSettingKey.SectionDesignBgColorVariant] ?? 'primary-100'
  const bgOverlayOpacity = (settings?.[ComponentSettingKey.SectionDesignBgOverlayOpacity] as number[])?.[0] ?? 100
  const withOverlay = settings?.[ComponentSettingKey.SectionDesignOverlay] ?? false
  const overlayColor = settings?.[ComponentSettingKey.SectionDesignOverlayColor] ?? 'primary'
  const reverseOrder = settings?.[ComponentSettingKey.SectionDesignReverseOrder] ?? false
  const variant = settings?.[ComponentSettingKey.SectionDesignVariant] ?? 'normal'
  const stickyPosition = settings?.[ComponentSettingKey.SectionDesignStickyPosition] ?? 'top'
  const stickyOffset = settings?.[ComponentSettingKey.SectionDesignStickyOffset] ?? 0
  const elevation = settings?.[ComponentSettingKey.SectionDesignElevation] ?? false

  // ========================= derived =========================

  const modifiedOverlayColor = overlayColor.includes('extended')
    ? overlayColor
    : BRANDKIT_ALL_COLOR_OPTIONS.includes(overlayColor)
      ? overlayColor
      : 'primary'

  // const adaptColor = settings?.[ComponentSettingKey.SectionDesignOverlayTextColor]

  // Process border settings
  const border = (settings[ComponentSettingKey.SectionDesignBorder] as string[]) ?? []
  const borderWidth = getSafely(settings, [ComponentSettingKey.SectionDesignBorderWidth], [0])[0]
  const borderStyle = settings[ComponentSettingKey.SectionDesignBorderStyle]
  const borderColor = settings[ComponentSettingKey.SectionDesignBorderColor]

  const borderProps: Record<string, string> = {}

  //  Generate border styles based on settings
  for (const b of border) {
    const token = `border-${b}`.replace(/([a-z])-([a-z])/g, (x) => {
      const s = x.split('-')
      return s[0] + s[1].toUpperCase()
    })

    borderProps[token] = `${borderWidth ?? 0}px ${borderStyle ?? 'solid'} ${
      borderColor ? `var(--color-${borderColor})` : 'var(--color-primary-0)'
    }`
  }

  // // Add padding if border is present
  // if (Object.keys(borderProps).length > 0 && borderWidth > 0) {
  //   borderProps['padding'] = '.5rem'
  // }

  // Background
  const backgroundSettings = settings?.[ComponentSettingKey.SectionDesignBackground] as SectionBackgroundGroupProps[]

  const getBackgroundUrl = (
    backgroundSettings: SectionBackgroundGroupProps[],
    type: 'image' | 'video'
  ): DefaultVariableResult => {
    return (backgroundSettings?.find((item) => item.type === type)?.values?.url as any) ?? ''
  }

  const imageSrc = useParseVariableSelect<string>(
    getBackgroundUrl(backgroundSettings, 'image'),
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const videoSrc = useParseVariableSelect<string>(
    getBackgroundUrl(backgroundSettings, 'video'),
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const styles: Record<string, any> = (backgroundSettings || []).map((item, index) => {
    const zIndex = (backgroundSettings || []).length - index
    const STYLE_GENERATORS = sectionBackgroundGenerators(videoSrc, imageSrc, backgroundColorPage, mode)
    return STYLE_GENERATORS[item.type]?.(item, zIndex) || {}
  })

  const solidColorValues = (backgroundSettings || []).find((item) => item.type === 'color')?.values
  const solidColor = solidColorValues?.color || ''

  // Helper function to get mode value for current light/dark mode
  const getModeValue = (modeValue: SectionColorVariant | string | undefined): string => {
    if (!modeValue) return 'No Color'
    if (typeof modeValue === 'object' && mode in modeValue) {
      return modeValue[mode] as string
    }
    return modeValue as string
  }

  const currentMode = getModeValue(solidColorValues?.mode as SectionColorVariant | string | undefined)
  const colorValue =
    currentMode === 'No Color'
      ? currentParentBg
      : solidColorValues && typeof solidColor === 'object'
        ? (solidColor as SectionColorVariant)[mode]
        : solidColor

  // const overlaySectionColor = backgroundSettings.find((item) => item.type === 'overlay')
  // const overlaySectionColorValue =
  //   colorValue ||
  //   bgColorVariant ||
  //   (typeof overlaySectionColor?.values?.color === 'object'
  //     ? (overlaySectionColor?.values?.color as SectionColorVariant)[mode]
  //     : overlaySectionColor?.values?.color)

  // const adaptiveOverlayColor = {
  //   ...overlaySectionColor,
  //   values: {
  //     ...overlaySectionColor?.values,
  //     color: overlaySectionColorValue,
  //   },
  // }

  // const updatedBackgroundSettings = backgroundSettings.map((bs) => (bs.type === 'overlay' ? adaptiveOverlayColor : bs))

  // inject new theme variables to handle form colors
  function injectFormThemeVariables() {
    return currentMode === 'No Color'
      ? {}
      : (createFormThemeVariables({
          backgroundColor: colorValue as ColorVariant,
        }) as any)
  }

  const bgLayerStyle: CSSProperties = {
    backgroundColor: `var(--md-sys-color-${bgColorVariant ?? 'primary'})`,
    opacity: bgOverlayOpacity / 100,
    zIndex: -1,
  }

  if (bgImg) {
    bgLayerStyle.backgroundImage = `url(${bgImg})`
    bgLayerStyle.backgroundPosition = 'center center'
    bgLayerStyle.backgroundSize = 'cover'
  }

  // get adaptive text color
  const textColor = getAdaptiveColorV2(
    bgColorVariant,
    0.98,
    withOverlay ? opacity : undefined,
    withOverlay ? modifiedOverlayColor : undefined
  )

  // ========================= render =========================

  // Prepare the settings object for CompSection
  const sectionSettings: CompSectionSettings = {
    style: {
      ...injectFormThemeVariables(),
      color: textColor,
      borderRadius: parseCornerRadius(cornerRadius),
      [`${TW_UI_CSS_VARIABLES.TEXT.active}`]: textColor,
    },
    bgLayerStyle,
    withOverlay,
    overlayColor: modifiedOverlayColor,
    opacity,
    verticalAlign,
    horizontalAlign,
    itemGap,
    sameHeight,
    backgroundSettings,
    videoSrc,
    imageSrc,
    bgStyles: styles,
    layout,
    orientation,
    borderProps,
    reverseOrder,
    variant,
    stickyPosition,
    elevation,
    stickyOffset,
    component,
    hoverEffect,
    currentParentBg,
    heightMode,
    height,
  }

  return (
    <BaseComponent
      id={component._id}
      width={width}
      alignment={alignment}
      margin={margin}
      padding={padding}
      renderAsChild={(props) =>
        children({
          baseProps: props,
          sectionSettings,
          component,
        })
      }
    />
  )
}
